/**
 * Shared distribution chart component for popup pages
 * Displays a horizontal bar chart grouped by a selected category
 */
import React from 'react';
import {
  Card,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ais-ui/ui-core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

export interface ChartDataItem {
  name: string;
  count: number;
}

export interface ChartOption {
  value: string;
  label: string;
}

export interface PopupDistributionChartProps {
  /** Chart data - array of { name, count } */
  data: ChartDataItem[];
  /** Currently selected grouping option */
  groupBy: string;
  /** Handler for when grouping changes */
  onGroupByChange: (value: string) => void;
  /** Available grouping options */
  options: ChartOption[];
  /** Chart title */
  title: string;
  /** Text to show when no data */
  noDataText?: string;
  /** Chart height in pixels */
  height?: number;
  /** Optional custom colors */
  colors?: string[];
}

const DEFAULT_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--primary) / 0.8)',
  'hsl(var(--primary) / 0.6)',
  'hsl(var(--primary) / 0.4)',
  'hsl(var(--primary) / 0.3)',
];

/**
 * Reusable distribution chart for popup pages
 * Shows a horizontal bar chart with selectable grouping
 */
export const PopupDistributionChart: React.FC<PopupDistributionChartProps> = ({
  data,
  groupBy,
  onGroupByChange,
  options,
  title,
  noDataText = 'No data',
  height = 300,
  colors = DEFAULT_COLORS,
}) => {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">{title}</h3>
        <Select value={groupBy} onValueChange={onGroupByChange}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {data.length === 0 ? (
        <div className="flex justify-center items-center h-64 text-muted-foreground">
          {noDataText}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ left: 100, right: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} />
            <XAxis type="number" />
            <YAxis
              type="category"
              dataKey="name"
              width={100}
              tick={{ fontSize: 12 }}
            />
            <Tooltip />
            <Bar dataKey="count" name="Vessels">
              {data.map((_, index) => (
                <Cell key={index} fill={colors[index % colors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};
